import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { CircularProgress, IconButton, Link, Paper, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import FileIcon from "@entrilia/shared/components/FileIcon";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { api } from "../../../../api/client";
import type { ChangeRequestAttachment } from "../../../../api/types/changeRequestsTypes";
import type { FileInfo } from "../../../../api/types/commonApiTypes";
import InvestorPortalPreviewFileDialog from "../../../common/InvestorPortalPreviewFileDialog";

interface Props {
  investorId: string;
  attachments: ChangeRequestAttachment[];
  uploadingFileName?: string;
  onRemove?: (attachment: ChangeRequestAttachment) => void;
}

const getAttachmentFileInfo = withErrorHandling(api.changeRequests.getAttachmentFileInfo);

const ChangeRequestAttachmentsList = ({ investorId, attachments, uploadingFileName, onRemove }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const [previewFileInfo, setPreviewFileInfo] = useState<FileInfo>();
  const [loadingPreviewId, setLoadingPreviewId] = useState<string>();

  if (attachments.length === 0 && !uploadingFileName) {
    return null;
  }

  const handlePreview = async (attachment: ChangeRequestAttachment) => {
    if (loadingPreviewId) {
      return;
    }

    setLoadingPreviewId(attachment.dataCatalogId);
    const [fileInfo, error] = await getAttachmentFileInfo(investorId, attachment.dataCatalogId);
    setLoadingPreviewId(undefined);
    if (error) {
      logError(error, "[ChangeRequestAttachmentsList] getAttachmentFileInfo");
      sendNotificationError(error.message);
      return;
    }

    setPreviewFileInfo(fileInfo);
  };

  return (
    <>
      <Stack spacing={1}>
        {attachments.map((attachment) => (
          <Paper key={attachment.dataCatalogId} variant="outlined" sx={{ px: 1.5, py: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              {loadingPreviewId === attachment.dataCatalogId ? (
                <CircularProgress size={20} />
              ) : (
                <FileIcon fileExtension={getFileExtension(attachment.name)} />
              )}
              <Link
                component="button"
                noWrap
                underline="hover"
                onClick={() => handlePreview(attachment)}
                sx={{ flexGrow: 1, textAlign: "left" }}
              >
                {attachment.name}
              </Link>
              {onRemove && (
                <IconButton aria-label={`Remove ${attachment.name}`} onClick={() => onRemove(attachment)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          </Paper>
        ))}
        {uploadingFileName && (
          <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <CircularProgress size={20} />
              <Typography noWrap color="textSecondary">
                Uploading {uploadingFileName}...
              </Typography>
            </Stack>
          </Paper>
        )}
      </Stack>
      {previewFileInfo && (
        <InvestorPortalPreviewFileDialog
          url={previewFileInfo.downloadUrl}
          fileName={previewFileInfo.fileName}
          preventDownload
          onClose={() => setPreviewFileInfo(undefined)}
        />
      )}
    </>
  );
};

export default ChangeRequestAttachmentsList;
